import React from 'react';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  ChevronRight,
  FileSpreadsheet,
  FileUp,
  GripVertical,
  Layers,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  Search,
  Trash2,
  Upload,
  X
} from 'lucide-react';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import { Card } from '../ui/Card';
import { FieldGroup, FieldLabel, Input } from '../ui/Input';
import Select from '../ui/Select';
import SectionHeading from '../ui/SectionHeading';
import Tooltip from '../ui/Tooltip';
import MapArea from './MapArea';
import { LayoutDashboard, Lock } from 'lucide-react';
import { cn } from '../../lib/cn';
import {
  buildRouteStopCode,
  getRouteDisplayName,
  highlightMatch
} from '../../utils/routeManagementHelpers';
import { useAuth } from '../../hooks/useAuth';

export default function RoutesPage({ controller }) {
  const {
    addStopToRoute,
    cancelRouteEdit,
    cancelSaveRouteConfirm,
    clearSelectedRouteFiles,
    derivedRouteName,
    editingRoute,
    handleListDragEnd,
    handleListDragEnter,
    handleListDragStart,
    handleRouteImport,
    handleRouteImportFileChange,
    handleSaveRoute,
    jumpToCreateStop,
    moveStop,
    pendingMissingStopIds,
    removeStopFromRoute,
    resetRouteStopToMaster,
    describeStopOverride,
    requestSaveRoute,
    resolveStopMasterName,
    routeBuilderMode,
    routeDirection,
    routeImportFiles,
    routeImporting,
    routeImportInputRef,
    routeImportParsing,
    routeImportPreviews,
    routeNumber,
    routeSaveConfirm,
    routeSearchOptions,
    routeSearchRef,
    routeStops,
    saving,
    setRouteBuilderMode,
    setRouteDirection,
    setRouteNumber,
    setStopSearch,
    stopSearch,
    stops,
    switchTab,
    viewingRoute,
    viewingRouteStops,
    loadRouteForEdit,
    toggleViewRoute,
    handleDeleteRoute,
    focusRoute,
    selectedRouteList,
    routeColorMap
  } = controller;

  const { isEditor } = useAuth();

  if (!isEditor) {
    return (
      <div className="flex-1 min-h-0 flex items-center justify-center p-10">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl shadow-soft p-8 text-center">
          <div className="h-12 w-12 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mx-auto">
            <Lock className="h-5 w-5" />
          </div>
          <h2 className="text-[18px] font-semibold text-slate-900 mt-4">Read-only access</h2>
          <p className="text-[13px] text-slate-500 mt-2">
            Your account is provisioned as a viewer. You can browse and inspect routes from the dashboard, but creating or editing routes requires editor permissions.
          </p>
          <div className="mt-5">
            <Button
              variant="primary"
              size="sm"
              leftIcon={LayoutDashboard}
              onClick={() => switchTab('data')}
            >
              Back to dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const stopSearchQuery = stopSearch.trim();
  const exactSearchMatch = stopSearchQuery
    ? stops.find(
        (stop) =>
          String(stop.stopId || '').toLowerCase() === stopSearchQuery.toLowerCase() ||
          stop.stopName.toLowerCase() === stopSearchQuery.toLowerCase()
      )
    : null;
  const showCreateStopFromSearch =
    stopSearchQuery && routeSearchOptions.length === 0 && !exactSearchMatch;

  return (
    <div className="flex-1 min-h-0 flex">
      <aside className="w-[400px] xl:w-[440px] shrink-0 h-full bg-white border-r border-slate-200 flex flex-col min-h-0">
        {viewingRoute && !editingRoute ? (
          <RouteViewPanel
            name={getRouteDisplayName(viewingRoute, resolveStopMasterName)}
            route={viewingRoute}
            viewingRouteStops={viewingRouteStops}
            stops={stops}
            isEditor={isEditor}
            onEdit={() => loadRouteForEdit(viewingRoute)}
            onClose={() => toggleViewRoute(viewingRoute)}
            onDelete={() =>
              handleDeleteRoute(
                viewingRoute._id,
                getRouteDisplayName(viewingRoute, resolveStopMasterName)
              )
            }
          />
        ) : (
        <>
        {editingRoute && (
          <div className="mx-4 mt-4 flex items-start justify-between gap-3 rounded-md border border-brand-200 bg-brand-50 px-3 py-2">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-700">
                Editing route
              </p>
              <p className="text-[13px] font-semibold text-brand-900 truncate mt-0.5">
                {getRouteDisplayName(editingRoute, resolveStopMasterName)}
              </p>
            </div>
            <Tooltip label="Cancel">
              <Button variant="ghost" size="iconSm" onClick={cancelRouteEdit}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </Tooltip>
          </div>
        )}

        {/* <> */}
        <div className="flex-1 min-h-0 overflow-y-auto p-5 flex flex-col gap-5">
          <section className="flex flex-col gap-3">
            <SectionHeading step={1} title="Route details" />

            <FieldGroup>
              <FieldLabel>Route name</FieldLabel>
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[13px] text-slate-700 min-h-[36px] flex items-center">
                {derivedRouteName ? (
                  <span className="font-medium truncate">{derivedRouteName}</span>
                ) : (
                  <span className="text-slate-400 italic">
                    Auto-generated from first → last stop
                  </span>
                )}
              </div>
            </FieldGroup>

            <div className="grid grid-cols-2 gap-3">
              <FieldGroup>
                <FieldLabel>Route no.</FieldLabel>
                <Input
                  value={routeNumber}
                  onChange={(e) => setRouteNumber(e.target.value)}
                  placeholder="e.g. 6"
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Direction</FieldLabel>
                <Select value={routeDirection} onChange={(e) => setRouteDirection(e.target.value)}>
                  <option value="">Select…</option>
                  <option value="Up">Up (1)</option>
                  <option value="Down">Down (0)</option>
                </Select>
              </FieldGroup>
            </div>
          </section>

          <section className="flex flex-col gap-3">
            <SectionHeading
              step={2}
              title="Build route"
              action={null}
            />

            {/* File import feature disabled */}
            <div className="flex flex-col gap-2" ref={routeSearchRef}>
                <div className="relative">
                  <Input
                    leftIcon={Search}
                    value={stopSearch}
                    onChange={(e) => setStopSearch(e.target.value)}
                    placeholder="Search master stops by name or StopID"
                  />
                  {stopSearchQuery && (
                    <div className="absolute left-0 right-0 top-full mt-1 z-30 max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-pop animate-slide-up">
                      {routeSearchOptions.length > 0 ? (
                        routeSearchOptions.map((stop) => {
                          const masterIndex = stops.findIndex((item) => item._id === stop._id);
                          return (
                            <button
                              key={stop._id}
                              type="button"
                              onMouseDown={() => addStopToRoute(stop)}
                              className="w-full text-left px-3 py-2 hover:bg-brand-50 transition-colors border-b border-slate-100 last:border-b-0"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="h-5 w-5 rounded-full bg-brand-100 text-brand-700 text-[10px] font-semibold flex items-center justify-center shrink-0 tabular-nums">
                                  {masterIndex + 1}
                                </span>
                                <span className="text-[13px] font-medium text-slate-900 truncate">
                                  {highlightMatch(stop.stopName, stopSearch)}
                                </span>
                              </div>
                              <p className="text-[11px] font-mono text-slate-500 mt-1 pl-7 truncate">
                                StopID: {highlightMatch(String(stop.stopId || ''), stopSearch)}
                                {' · '}
                                {stop.lat.toFixed(6)}, {stop.lng.toFixed(6)}
                              </p>
                            </button>
                          );
                        })
                      ) : showCreateStopFromSearch ? (
                        <button
                          type="button"
                          onMouseDown={() => jumpToCreateStop(stopSearchQuery)}
                          className="w-full text-left px-3 py-3 hover:bg-brand-50 transition-colors"
                        >
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-[12.5px] font-semibold text-slate-800">
                                "{stopSearchQuery}" not in StopMaster
                              </p>
                              <p className="text-[11.5px] text-brand-700 mt-0.5 inline-flex items-center gap-1">
                                <Plus className="h-3 w-3" /> Create this stop
                              </p>
                            </div>
                          </div>
                        </button>
                      ) : (
                        <div className="px-3 py-3 text-[12.5px] text-slate-400 italic text-center">
                          No matching stops
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </section>

          {routeStops.length > 0 && (
            <section className="flex flex-col gap-3">
              <SectionHeading
                step={3}
                title="Route stops"
                action={
                  <div className="flex items-center gap-2">
                    <Badge variant="brand" size="sm">{routeStops.length}</Badge>
                    <span className="text-[11px] text-slate-400">Drag to reorder</span>
                  </div>
                }
              />

              <div className="rounded-md border border-slate-200 overflow-hidden bg-slate-50/50">
                <div className="max-h-[220px] overflow-y-auto">
                  {routeStops.map((stop, index) => {
                    const masterCode = stops.find((m) => m._id === stop._id)?.stopId;
                    const previewCode = buildRouteStopCode({
                      stopCode: masterCode,
                      routeNumber,
                      direction: routeDirection,
                      sequence: index + 1
                    });
                    // Which of this row's values the route owns rather than
                    // inherits from the stop master.
                    const override = describeStopOverride(stop);
                    return (
                    <div
                      key={`${stop._id}-${index}`}
                      draggable
                      onDragStart={() => handleListDragStart(index)}
                      onDragEnter={() => handleListDragEnter(index)}
                      onDragEnd={handleListDragEnd}
                      onDragOver={(e) => e.preventDefault()}
                      className="flex items-center gap-2 px-2 py-1.5 border-b border-slate-100 last:border-b-0 bg-white hover:bg-slate-50 transition-colors cursor-grab active:cursor-grabbing"
                    >
                      <GripVertical className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                      <span className="h-5 w-5 rounded-full bg-brand-100 text-brand-700 text-[10px] font-semibold flex items-center justify-center shrink-0 tabular-nums">
                        {index + 1}
                      </span>
                      {/*
                        The name is read-only here on purpose. A stop is renamed in
                        the Stops workspace, where the change applies everywhere the
                        stop is used. Routes saved before that rule can still carry a
                        name of their own — the note below flags it, and the reset
                        button hands the entry back to the stop master.
                      */}
                      <span className="flex-1 min-w-0">
                        <span
                          title={stop.stopName || ''}
                          className={cn(
                            'block w-full text-[12.5px] font-medium truncate',
                            override.name ? 'text-brand-800' : 'text-slate-800'
                          )}
                        >
                          {stop.stopName || '—'}
                        </span>
                        <span className="block font-mono text-[10.5px] text-slate-400 truncate">
                          {previewCode}
                        </span>
                        {(override.name || override.position) && (
                          <span className="block text-[10px] text-brand-600 truncate">
                            {override.name && override.position
                              ? 'Name and position set for this route'
                              : override.name
                                ? `Renamed on this route (master: ${override.masterName})`
                                : 'Position set for this route'}
                          </span>
                        )}
                      </span>
                      <div className="flex items-center gap-0.5 shrink-0">
                        {(override.name || override.position) && (
                          <Tooltip label="Reset to the stop master's name and position">
                            <button
                              type="button"
                              onClick={() => resetRouteStopToMaster(index)}
                              className="h-6 w-6 inline-flex items-center justify-center rounded text-brand-600 hover:bg-brand-50"
                            >
                              <RotateCcw className="h-3 w-3" />
                            </button>
                          </Tooltip>
                        )}
                        <Tooltip label="Up">
                          <button
                            type="button"
                            onClick={() => moveStop(index, -1)}
                            disabled={index === 0}
                            className="h-6 w-6 inline-flex items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-30 disabled:hover:bg-transparent"
                          >
                            <ArrowUp className="h-3 w-3" />
                          </button>
                        </Tooltip>
                        <Tooltip label="Down">
                          <button
                            type="button"
                            onClick={() => moveStop(index, 1)}
                            disabled={index === routeStops.length - 1}
                            className="h-6 w-6 inline-flex items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-30 disabled:hover:bg-transparent"
                          >
                            <ArrowDown className="h-3 w-3" />
                          </button>
                        </Tooltip>
                        <Tooltip label="Remove">
                          <button
                            type="button"
                            onClick={() => removeStopFromRoute(index)}
                            className="h-6 w-6 inline-flex items-center justify-center rounded text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}
        </div>

        <footer className="shrink-0 flex items-center gap-2 px-5 py-3 bg-slate-50/60 border-t border-slate-200">
          {/* File import feature disabled */}
          <div className="flex-1" />
          <Button
            variant="primary"
            size="md"
            leftIcon={Save}
            loading={saving}
            disabled={
              !routeNumber.trim() ||
              !routeDirection.trim() ||
              routeStops.length < 2
            }
            onClick={requestSaveRoute}
          >
            {editingRoute ? 'Update route' : 'Create route'}
          </Button>
        </footer>
        </>
        )}
      </aside>

      <div className="flex-1 min-w-0 relative">
        <MapArea controller={controller} />
        {selectedRouteList.length > 1 && (
          <SelectedRoutesDrawer
            routes={selectedRouteList}
            colorMap={routeColorMap}
            focusedId={viewingRoute?._id}
            onFocus={focusRoute}
            resolveStopMasterName={resolveStopMasterName}
          />
        )}
      </div>

      {routeSaveConfirm && (
        <ConfirmSaveRouteDialog
          confirm={routeSaveConfirm}
          saving={saving}
          onCancel={cancelSaveRouteConfirm}
          onConfirm={handleSaveRoute}
        />
      )}
    </div>
  );
}

function SelectedRoutesDrawer({ routes, colorMap, focusedId, onFocus, resolveStopMasterName }) {
  const [collapsed, setCollapsed] = React.useState(false);

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        className="absolute top-3 right-3 z-[1001] inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-white/95 backdrop-blur-md border border-slate-200 shadow-pop text-[12.5px] font-semibold text-slate-700 hover:border-brand-300 hover:text-brand-700 transition-colors"
      >
        <Layers className="h-3.5 w-3.5 text-brand-600" />
        {routes.length} routes
      </button>
    );
  }

  return (
    <div className="absolute top-3 right-3 z-[1001] w-[300px] max-w-[calc(100%-1.5rem)] bg-white/95 backdrop-blur-md border border-slate-200 shadow-pop rounded-xl flex flex-col max-h-[calc(100%-1.5rem)] animate-slide-up">
      <div className="flex items-center justify-between gap-2 px-3.5 py-2.5 border-b border-slate-100">
        <div className="flex items-center gap-1.5 text-slate-600 min-w-0">
          <Layers className="h-3.5 w-3.5 text-brand-600 shrink-0" />
          <span className="text-[10.5px] font-semibold uppercase tracking-wider truncate">
            Selected routes
          </span>
          <Badge variant="brand" size="sm">{routes.length}</Badge>
        </div>
        <Tooltip label="Collapse">
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            className="h-6 w-6 inline-flex items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 shrink-0"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </Tooltip>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-1.5">
        {routes.map((route) => {
          const isFocused = route._id === focusedId;
          const color = colorMap?.get(route._id);
          return (
            <button
              key={route._id}
              type="button"
              onClick={() => onFocus(route)}
              className={cn(
                'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors',
                isFocused
                  ? 'bg-brand-50 ring-1 ring-brand-300'
                  : 'hover:bg-slate-50'
              )}
            >
              <span
                className="h-3 w-3 rounded-full shrink-0 ring-2 ring-white shadow-sm"
                style={{ backgroundColor: color }}
              />
              <div className="min-w-0 flex-1">
                <p
                  className={cn(
                    'text-[12.5px] truncate',
                    isFocused ? 'font-bold text-slate-900' : 'font-medium text-slate-700'
                  )}
                >
                  {getRouteDisplayName(route, resolveStopMasterName)}
                </p>
                <p className="text-[10.5px] text-slate-400 mt-0.5 truncate">
                  Route {route.routeNumber} · {route.direction} · {route.stops.length} stops
                </p>
              </div>
              {isFocused && (
                <span className="text-[9px] font-semibold uppercase tracking-wide text-brand-700 bg-brand-100 rounded px-1.5 py-0.5 shrink-0">
                  Focused
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ImportRouteBlock({
  routeImportInputRef,
  routeImportFiles,
  handleRouteImportFileChange,
  clearSelectedRouteFiles,
  routeImportParsing,
  routeImportPreviews,
  pendingMissingStopIds,
  jumpToCreateStop
}) {
  const [active, setActive] = React.useState(false);
  return (
    <Card className="border-slate-200 bg-gradient-to-b from-white to-brand-50/30 p-4 flex flex-col gap-3">
      <div>
        <p className="text-[13px] font-semibold text-slate-800">One route per file</p>
        <p className="text-[11.5px] leading-relaxed text-slate-500 mt-1">
          Required columns:{' '}
          <span className="font-mono text-slate-700">SequenceNo</span>,{' '}
          <span className="font-mono text-slate-700">StopID</span>,{' '}
          <span className="font-mono text-slate-700">Direction</span>,{' '}
          <span className="font-mono text-slate-700">RouteNo</span>
        </p>
        <p className="text-[11.5px] leading-relaxed text-slate-500 mt-1">
          StopName, Lat &amp; Long are looked up from StopMaster using StopID. RouteName is built from the first → last stop.
        </p>
      </div>

      <button
        type="button"
        onClick={() => routeImportInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setActive(true);
        }}
        onDragLeave={() => setActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setActive(false);
          const files = Array.from(e.dataTransfer.files || []);
          if (files.length > 0) handleRouteImportFileChange({ target: { files } });
        }}
        className={cn(
          'w-full flex flex-col items-center justify-center text-center rounded-lg border-2 border-dashed py-6 px-4 transition-all duration-150',
          active
            ? 'border-brand-400 bg-brand-50/60 shadow-soft'
            : 'border-slate-200 bg-white hover:border-brand-300 hover:bg-brand-50/30'
        )}
      >
        <div className="h-10 w-10 rounded-full bg-brand-100 text-brand-600 flex items-center justify-center mb-2">
          <FileSpreadsheet className="h-5 w-5" />
        </div>
        <p className="text-[13.5px] font-semibold text-slate-700">Choose route files</p>
        <p className="text-[11.5px] text-slate-500 mt-1">
          .xlsx · .xls · .csv — multiple OK
        </p>
      </button>

      <input
        ref={routeImportInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        multiple
        className="hidden"
        onChange={handleRouteImportFileChange}
      />

      {routeImportFiles.length > 0 && (
        <div className="rounded-md border border-slate-200 overflow-hidden bg-white">
          <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
            <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
              Selected files ({routeImportFiles.length})
            </span>
            <button
              type="button"
              className="text-[11.5px] font-medium text-slate-500 hover:text-slate-800"
              onClick={clearSelectedRouteFiles}
            >
              Clear
            </button>
          </div>
          <div className="max-h-32 overflow-y-auto">
            {routeImportFiles.map((file) => (
              <div
                key={`${file.name}-${file.lastModified}`}
                className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 last:border-b-0"
              >
                <FileUp className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                <span className="text-[12.5px] text-slate-700 truncate">{file.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {routeImportParsing && (
        <p className="text-[12px] text-slate-500 italic">Parsing files…</p>
      )}

      {!routeImportParsing && routeImportPreviews.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
            Preview
          </p>
          {routeImportPreviews.map((preview, index) => (
            <PreviewCard
              key={`${preview.fileName}-${index}`}
              preview={preview}
              jumpToCreateStop={jumpToCreateStop}
            />
          ))}

          {pendingMissingStopIds.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
              <p className="font-semibold mb-1 inline-flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" /> Missing StopIDs in StopMaster
              </p>
              <p className="leading-relaxed">
                Create these stops first, then the import will resolve automatically:
              </p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {pendingMissingStopIds.map((stopId) => (
                  <button
                    key={stopId}
                    type="button"
                    onClick={() => jumpToCreateStop(stopId)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white border border-amber-300 text-amber-900 hover:bg-amber-100 font-mono text-[11.5px]"
                  >
                    <Plus className="h-3 w-3" /> {stopId}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function PreviewCard({ preview, jumpToCreateStop }) {
  if (preview.error) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2">
        <p className="text-[12px] font-semibold text-rose-800 truncate">
          {preview.fileName}
        </p>
        <p className="text-[11.5px] text-rose-700 mt-0.5">{preview.error}</p>
      </div>
    );
  }

  const hasMissing = (preview.missingStopIds || []).length > 0;

  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2',
        hasMissing
          ? 'border-amber-200 bg-amber-50/50'
          : 'border-emerald-200 bg-emerald-50/40'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[12px] font-semibold text-slate-800 truncate">
            {preview.fileName}
          </p>
          <p className="text-[11.5px] text-slate-600 mt-0.5 truncate">
            {preview.routeName || (
              <span className="italic text-slate-400">
                Route name pending — fix missing stops
              </span>
            )}
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5 font-mono">
            Route {preview.routeNumber} · {preview.direction} · {preview.stops.length}{' '}
            stops
          </p>
        </div>
        {hasMissing ? (
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
        ) : (
          <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
        )}
      </div>

      {hasMissing && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {preview.missingStopIds.map((stopId) => (
            <button
              key={stopId}
              type="button"
              onClick={() => jumpToCreateStop(stopId)}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white border border-amber-300 text-amber-900 hover:bg-amber-100 font-mono text-[11px]"
            >
              <Plus className="h-2.5 w-2.5" /> {stopId}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RouteViewPanel({ name, route, viewingRouteStops, stops, isEditor, onEdit, onClose, onDelete }) {
  const humanStopId = (stop) =>
    stops.find((m) => String(m._id) === String(stop.stopId))?.stopId || '';

  return (
    <>
      <header className="shrink-0 px-5 pt-4 pb-3 border-b border-slate-100">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-emerald-600">
              Viewing route
            </p>
            <h2 className="text-[17px] font-semibold text-slate-900 tracking-tight mt-1 truncate">
              {name}
            </h2>
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              <Badge variant="outline" size="sm" className="font-mono">{route.routeId || 'No ID'}</Badge>
              <Badge variant="brand" size="sm">Route {route.routeNumber}</Badge>
              <Badge
                variant={route.direction.toLowerCase().includes('up') ? 'success' : 'warning'}
                size="sm"
              >
                {route.direction}
              </Badge>
            </div>
          </div>
          <Tooltip label="Close">
            <Button variant="ghost" size="iconSm" onClick={onClose}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </Tooltip>
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Stops on this route
          </p>
          <Badge variant="brand" size="sm">{viewingRouteStops.length}</Badge>
        </div>

        <div className="rounded-md border border-slate-200 overflow-hidden">
          {viewingRouteStops.map((stop, index) => (
            <div
              key={`${stop.stopId}-${index}`}
              className="flex items-center gap-2.5 px-2.5 py-2 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/70 transition-colors"
            >
              <span className="h-5 w-5 rounded-full bg-brand-100 text-brand-700 text-[10px] font-semibold flex items-center justify-center shrink-0 tabular-nums">
                {stop.sequence}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[12.5px] font-medium text-slate-800 truncate">{stop.stopName}</p>
                <p className="font-mono text-[10.5px] text-slate-400 truncate">
                  {humanStopId(stop) || '—'} · {stop.routeStopCode || '—'}
                </p>
              </div>
              <span className="font-mono text-[10px] text-slate-400 shrink-0">
                {Number(stop.lat).toFixed(4)}, {Number(stop.lng).toFixed(4)}
              </span>
            </div>
          ))}
        </div>
      </div>

      <footer className="shrink-0 flex items-center gap-2 px-5 py-3 bg-slate-50/60 border-t border-slate-200">
        {isEditor && (
          <Button variant="primary" size="md" leftIcon={Pencil} onClick={onEdit}>
            Edit route
          </Button>
        )}
        <div className="flex-1" />
        {isEditor && (
          <Tooltip label="Delete route">
            <Button variant="ghost" size="icon" onClick={onDelete} className="text-rose-600 hover:bg-rose-50">
              <Trash2 className="h-4 w-4" />
            </Button>
          </Tooltip>
        )}
        <Button variant="secondary" size="md" onClick={onClose}>
          Close
        </Button>
      </footer>
    </>
  );
}

function ConfirmSaveRouteDialog({ confirm, saving, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/30 backdrop-blur-[2px] animate-fade-in">
      <div className="w-[420px] max-w-[92vw] bg-white rounded-xl shadow-pop border border-slate-200 p-5 animate-slide-up">
        <h3 className="text-[16px] font-semibold text-slate-900">
          {confirm.isUpdate ? 'Update route?' : 'Create route?'}
        </h3>
        <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-[12.5px] text-slate-700 space-y-1">
          <p>
            <span className="font-semibold text-slate-500">Name:</span>{' '}
            <span className="font-medium">{confirm.routeName || '—'}</span>
          </p>
          <p>
            <span className="font-semibold text-slate-500">RouteNo:</span>{' '}
            <span className="font-mono">{confirm.routeNumber}</span>
          </p>
          <p>
            <span className="font-semibold text-slate-500">Direction:</span>{' '}
            <span>{confirm.direction}</span>
          </p>
          <p>
            <span className="font-semibold text-slate-500">Stops:</span>{' '}
            <span className="tabular-nums">{confirm.stopCount}</span>
          </p>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="ghost" size="md" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="md" loading={saving} onClick={onConfirm}>
            {confirm.isUpdate ? 'Confirm update' : 'Confirm create'}
          </Button>
        </div>
      </div>
    </div>
  );
}
